import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import { categories, type Category } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSort, setActiveSort] = useState<'hot' | 'new' | 'rating'>('hot');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug) || categories[0],
    [slug]
  );

  const categoryVideos = useMemo(() => {
    let list = videos.filter((v) => v.categorySlug === (slug || 'aiqing-ju'));
    if (activeSort === 'new') return [...list].filter((v) => v.isNew).concat(list.filter((v) => !v.isNew));
    if (activeSort === 'rating') return [...list].sort((a, b) => b.rating - a.rating);
    return [...list].sort((a, b) => (b.isHot ? 1 : 0) - (a.isHot ? 1 : 0));
  }, [slug, activeSort]);

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 暖暖爱视频免费观看 | 高清在线播放`}
        description={`暖暖爱视频${currentCategory.name}分类，${currentCategory.description}。提供高清完整版在线播放服务，免费观看全集视频内容。`}
        keywords={`${currentCategory.name},暖暖爱视频免费,暖暖爱免费观看,暖暖爱高清视频,在线播放`}
        canonicalUrl={`https://www.nuannuanai.com/category/${slug || 'aiqing-ju'}`}
      />

      <Navbar />
      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '分类浏览', path: '/category/aiqing-ju' },
          { label: currentCategory.name },
        ]}
      />

      <main>
        {/* Category Header */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-4 mb-6">
              <span className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                <i className={`${currentCategory.icon} text-2xl md:text-3xl`}></i>
              </span>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  {currentCategory.description} · 共 {currentCategory.videoCount} 部视频
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-16 z-30 bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {categories.map((cat: Category) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    (slug || 'aiqing-ju') === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'text-foreground-500 hover:text-foreground-900 hover:bg-background-100'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Sort & Video Grid */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {/* Sort tabs */}
            <div className="flex items-center gap-2 mb-8">
              {[
                { key: 'hot', label: '热门' },
                { key: 'new', label: '最新' },
                { key: 'rating', label: '评分最高' },
              ].map((s) => (
                <button
                  key={s.key}
                  onClick={() => setActiveSort(s.key as 'hot' | 'new' | 'rating')}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeSort === s.key
                      ? 'bg-secondary-500 text-white'
                      : 'text-foreground-500 bg-background-50 hover:text-foreground-900'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>

            {/* Video Grid */}
            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {categoryVideos.map((video) => (
                  <div
                    key={video.id}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={video.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                        {video.duration}
                      </div>
                      {video.isNew && (
                        <div className="absolute top-2 left-2">
                          <span className="bg-accent-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            最新
                          </span>
                        </div>
                      )}
                      {video.isHot && !video.isNew && (
                        <div className="absolute top-2 left-2">
                          <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                            热门
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-1">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mb-3 line-clamp-2">
                        {video.description}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </span>
                          {video.views >= 10000 ? `${(video.views / 10000).toFixed(1)}万` : video.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-4 h-4 flex items-center justify-center text-accent-500">
                            <i className="ri-star-fill"></i>
                          </span>
                          {video.rating}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-300">
                  <i className="ri-video-line text-4xl"></i>
                </span>
                <p className="text-foreground-400">该分类暂无视频内容</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}